import { useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Chart as Chartjs, registerables } from 'chart.js';
import { Bar } from "react-chartjs-2";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
// UptimeBarCard configurations
import configs from "./config";
import { processUptimeCounts } from "utils/device";
import { processDevicesData } from "utils/device";
import { useSoftUIController } from "hooks/context";

Chartjs.register(...registerables);

// Setting default values for the props of UptimeBarCard
function UptimeBarCard({ devices, fetch_time = null, raw = true }) {

    const [controller] = useSoftUIController();
    const { userPermissions, deviceLayout } = controller;

    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

    var devicesData = raw ? (devices.data ? processDevicesData(devices.dataUpdatedAt, devices.data, showAdvancedDeviceLevel) : []) :
    (devices ? processDevicesData(fetch_time, devices, showAdvancedDeviceLevel) : []);
    const { count, uptime_status } = devicesData ? processUptimeCounts(devicesData) : { count: 0, uptime_status: [0, 0] };

    var uptime_chart_settings = {
        labels: ["Healthy", "Requires Attention"],
        datasets: {
            label: "All Active Devices",
            backgroundColors: ["success", "error"],
            data: uptime_status
        }
    }

    const { data, options } = configs(uptime_chart_settings.labels, uptime_chart_settings.datasets || {}, count);
    const plugins = [{
        id: 'sideLabels',
        afterDatasetsDraw(chart, args, pluginOptions) {
            const { ctx, data } = chart;
            ctx.save();

            chart.getDatasetMeta(0).data.forEach((dataPoint, index) => {
                ctx.font = 'bold 18px sans-serif';
                ctx.fillStyle = 'black';
                ctx.textAlign = 'right';
                ctx.fillText(data.datasets[0].data[index], dataPoint.x - 8, dataPoint.y + 6)
            })
        }
    }];

    return (
        <SoftBox py={1} px={2}
            sx={({ palette: { white }, functions: { rgba } }) => ({
                display: "flex",
                flexDirection: "row",
                alignItems: 'center',
                backgroundColor: rgba(white.main, 0.8),
                borderRadius: "16px",
                width: 'calc(100% - 32px)',
                height: 'calc(100% - 16px)',
                gap: 2,
            })}
        >
            <SoftBox zIndex={1} sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                minWidth: 'max-content',
            }}>
                <SoftBox display="flex" flexDirection="column">
                    <SoftTypography variant={deviceLayout == 'mobile' ? "subtitle2" : "subtitle1"} fontWeight="medium" lineHeight={1.3}>
                        All Active Devices
                    </SoftTypography>
                    {count ? (
                        <SoftTypography variant={deviceLayout == 'mobile' ? "mobileBody3" : "subtitle3"}>
                            {uptime_status[0]} / {count} Healthy.
                        </SoftTypography>
                    ) : null}
                </SoftBox>
            </SoftBox>

            {useMemo(
                () => (
                    <SoftBox zIndex={2} sx={{
                        flex: 1,
                        position: 'relative',
                        minWidth: 0,
                        minHeight: { xxs: '40px', md: '60px' },
                    }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                            <Bar
                                data={data}
                                options={options}
                                plugins={plugins}
                            />
                        </div>
                    </SoftBox>
                ),
                [data, options, plugins]
            )}
        </SoftBox>
    );
}

// Typechecking props for the UptimeBarCard
UptimeBarCard.propTypes = {
    devices: PropTypes.object,
};

export default UptimeBarCard;